/**
 * Document template editor — blocks left, live preview right.
 *
 * Spec: .claude/specs/document-templates.md (Package B, AC11-AC19).
 *
 * Its own full-width route rather than a Settings tab because SettingsPage is
 * capped at 860px, which is too narrow for two panes.
 *
 * The one thing this screen must get right is making the locked/AI boundary
 * impossible to misread. An admin who cannot tell at a glance which paragraphs
 * a model wrote will either trust all of the output or none of it.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { AsyncButton } from "../components/AsyncButton";
import {
  analyseDocTemplateImport, createDocTemplate, extractDocTemplateFile,
  getDocTemplate, getDocTemplateMergeFields, getDocTemplateStarter, getDocTemplateTypes,
  getDocTemplateVersions, getEmployees, previewDocTemplate, previewDocTemplatePdf,
  promoteDocTemplateVersion,
  restoreDocTemplateVersion, updateDocTemplate,
} from "../api";

const NAVY = "#1e3a8a";
const AI_BG = "#f0f9ff";
const AI_LINE = "#bae6fd";
const AI_FG = "#0369a1";

type Block = {
  id: string;
  kind: "static" | "ai";
  heading?: string | null;
  text?: string;
  instruction?: string;
  max_words?: number;
  fallback_text?: string;
  /** Present only on freshly imported blocks, so a field proposal can be undone. */
  original_text?: string;
};

type MergeFieldDef = { token: string; label: string; example: string };
type FieldGroups = Record<string, MergeFieldDef[]>;

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 11px", borderRadius: 6,
  border: "1px solid #e2e8f0", fontSize: 13, color: "#0f172a",
  boxSizing: "border-box", fontFamily: "inherit",
};
const labelStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: "#334155", display: "block", marginBottom: 5,
};
const btnPrimary: React.CSSProperties = {
  background: NAVY, color: "white", border: "1px solid transparent",
  padding: "8px 15px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13,
};
const btnSecondary: React.CSSProperties = {
  background: "white", color: "#334155", border: "1px solid #cbd5e1",
  padding: "8px 15px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13,
};
const btnGhost: React.CSSProperties = {
  background: "transparent", color: "#64748b", border: "none",
  padding: "6px 10px", cursor: "pointer", fontWeight: 600, fontSize: 12,
};

function newBlock(kind: "static" | "ai", n: number): Block {
  return kind === "static"
    ? { id: `b${n}`, kind: "static", heading: "", text: "" }
    : {
        id: `b${n}`, kind: "ai", heading: "", instruction: "",
        max_words: 80, fallback_text: "",
      };
}

export default function DocumentTemplateEditorPage() {
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const isNew = id === "new";
  const canPublish = ["admin", "admin_assistant"].includes(user?.role || "");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [docFamily, setDocFamily] = useState(search.get("family") || "hr_letter");
  const [docType, setDocType] = useState(search.get("type") || "appointment");
  const [typeLabel, setTypeLabel] = useState("");
  const [version, setVersion] = useState(0);
  const [isDefault, setIsDefault] = useState(false);
  const [liveVersion, setLiveVersion] = useState<number | null>(null);
  const [pending, setPending] = useState(false);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [fields, setFields] = useState<FieldGroups>({});
  const [problems, setProblems] = useState<string[]>([]);

  // Preview
  const [employees, setEmployees] = useState<any[]>([]);
  const [previewFor, setPreviewFor] = useState<number | "">("");
  const [preview, setPreview] = useState<{ content: string; fell_back: string[] } | null>(null);
  const [previewing, setPreviewing] = useState(false);

  // Import
  const [showImport, setShowImport] = useState(isNew && search.get("import") === "1");
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [importNote, setImportNote] = useState("");

  // Versions
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState<any[]>([]);

  const [fieldPickerFor, setFieldPickerFor] = useState<string | null>(null);
  const [hasStarter, setHasStarter] = useState(false);
  const [starterNote, setStarterNote] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [fieldRes, types] = await Promise.all([
          getDocTemplateMergeFields(), getDocTemplateTypes(),
        ]);
        setFields(fieldRes.groups || {});
        if (!isNew && id) {
          const tpl = await getDocTemplate(Number(id));
          setName(tpl.name);
          setDocFamily(tpl.doc_family);
          setDocType(tpl.doc_type);
          setTypeLabel(tpl.doc_type_label);
          setVersion(tpl.version);
          setIsDefault(tpl.is_default);
          setLiveVersion(tpl.live_version ?? null);
          setPending(!!tpl.has_pending_changes);
          setBlocks(tpl.blocks || []);
        } else {
          const match = (types || []).find(
            (t: any) => t.doc_family === docFamily && t.doc_type === docType);
          setTypeLabel(match?.label || docType);
          setHasStarter(!!match?.has_starter);
          setBlocks([newBlock("static", 1)]);
        }
        getEmployees().then((rows: any[]) => setEmployees(rows || [])).catch(() => {});
      } catch {
        toast("Could not load this template", "error");
        navigate("/settings?tab=templates");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const aiCount = useMemo(() => blocks.filter(b => b.kind === "ai").length, [blocks]);
  const hasFullName = useMemo(
    () => blocks.some(b => b.kind === "static" && (b.text || "").includes("{{employee_name}}")),
    [blocks],
  );

  /** Tokens in a block that are not in the published vocabulary (AC13/AC15).
      Mirrors the server rule so the author sees it while typing, but the server
      is still the gate — this is a convenience, not the enforcement. */
  const unknownTokens = useMemo(() => {
    const valid: Record<string, true> = {};
    Object.keys(fields).forEach(group => {
      (fields[group] || []).forEach(f => { valid[f.token] = true; });
    });
    const known = Object.keys(valid).length > 0;

    const out: Record<string, string[]> = {};
    blocks.forEach(b => {
      const text = `${b.text || ""} ${b.fallback_text || ""}`;
      const matches = text.match(/\{\{\s*[A-Za-z0-9_]+\s*\}\}/g) || [];
      const bad: string[] = [];
      matches.forEach(raw => {
        const token = raw.replace(/[{}\s]/g, "");
        if (known && !valid[token] && bad.indexOf(token) === -1) bad.push(token);
      });
      if (bad.length) out[b.id] = bad;
    });
    return out;
  }, [blocks, fields]);

  const blocked = Object.keys(unknownTokens).length > 0 || !hasFullName;

  function patch(blockId: string, changes: Partial<Block>) {
    setBlocks(bs => bs.map(b => (b.id === blockId ? { ...b, ...changes } : b)));
  }

  function move(blockId: string, delta: number) {
    setBlocks(bs => {
      const i = bs.findIndex(b => b.id === blockId);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= bs.length) return bs;
      const copy = [...bs];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  }

  function addBlock(kind: "static" | "ai") {
    setBlocks(bs => {
      const used = new Set(bs.map(b => b.id));
      let n = bs.length + 1;
      while (used.has(`b${n}`)) n += 1;
      return [...bs, newBlock(kind, n)];
    });
  }

  function insertField(blockId: string, token: string) {
    const b = blocks.find(x => x.id === blockId);
    if (!b) return;
    patch(blockId, { text: `${b.text || ""}{{${token}}}` });
    setFieldPickerFor(null);
  }

  function payload() {
    return blocks.map(b => {
      const { original_text, ...rest } = b;
      return rest;
    });
  }

  async function save() {
    if (!name.trim()) { toast("Give the template a name", "error"); return; }
    // AC15 reads as a UI guard, and the server rejecting is a worse experience
    // than the page refusing — the problems are already on screen.
    if (blocked) {
      toast("Fix the highlighted problems before saving", "error");
      return;
    }
    setSaving(true);
    setProblems([]);
    try {
      if (isNew) {
        const created = await createDocTemplate({
          doc_family: docFamily, doc_type: docType, name: name.trim(),
          blocks: payload(),
        });
        toast("Template created", "success");
        navigate(`/settings/document-templates/${created.id}`, { replace: true });
      } else {
        const saved = await updateDocTemplate(Number(id), {
          name: name.trim(), blocks: payload(),
        });
        setVersion(saved.version);
        setLiveVersion(saved.live_version ?? null);
        setPending(!!saved.has_pending_changes);
        toast(
          saved.has_pending_changes
            ? `Saved as v${saved.version} — an admin must approve it before it goes out`
            : `Saved as v${saved.version}`,
          "success",
        );
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (detail?.problems) {
        setProblems(detail.problems);
        toast("Fix the problems listed below", "error");
      } else {
        toast(detail?.message || detail || "Save failed", "error");
      }
    } finally {
      setSaving(false);
    }
  }

  async function openPdfPreview() {
    setPreviewing(true);
    try {
      const blob = await previewDocTemplatePdf({
        blocks: payload(),
        employee_id: previewFor || undefined,
        doc_type: docType,
      });
      const url = URL.createObjectURL(blob as Blob);
      window.open(url, "_blank");
      // Revoked on a delay: revoking immediately can race the new tab's load.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
      toast(e?.response?.data?.detail ? "Fix the problems first"
                                      : "Could not render the preview", "error");
    } finally {
      setPreviewing(false);
    }
  }

  async function runPreview(withAi: boolean) {
    setPreviewing(true);
    try {
      const out = await previewDocTemplate({
        blocks: payload(),
        employee_id: previewFor || undefined,
        with_ai: withAi,
      });
      setPreview(out);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (detail?.problems) setProblems(detail.problems);
      toast(detail?.problems ? "Fix the problems first" : "Preview failed", "error");
    } finally {
      setPreviewing(false);
    }
  }

  async function loadStarter() {
    try {
      const out = await getDocTemplateStarter(docFamily, docType);
      setBlocks(out.blocks);
      if (!name.trim()) setName(out.suggested_name);
      setStarterNote(out.note);
      setShowImport(false);
      setImportNote(
        `Loaded the HRxAI standard ${typeLabel.toLowerCase()} — ` +
        `${out.blocks.length} blocks. Edit any of it before saving.`,
      );
    } catch {
      toast("Could not load the standard wording", "error");
    }
  }

  async function runImport() {
    if (!importText.trim()) { toast("Paste the letter text first", "error"); return; }
    setImporting(true);
    try {
      const out = await analyseDocTemplateImport({
        text: importText, employee_id: previewFor || undefined,
      });
      setBlocks(out.blocks);
      setShowImport(false);
      setImportNote(
        out.needs_full_name
          ? "Imported. This letter never names the person in full — add {{employee_name}} to a fixed block before saving."
          : `Imported ${out.paragraph_count} paragraphs. ${out.ai_suggested.length} proposed as AI-written — review each one.`,
      );
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Could not read that letter", "error");
    } finally {
      setImporting(false);
    }
  }

  async function uploadFile(file: File) {
    setImporting(true);
    try {
      const out = await extractDocTemplateFile(file, docType);
      setImportText(out.text);
      toast(`Read ${out.chars.toLocaleString()} characters from ${out.filename}`, "success");
    } catch (e: any) {
      toast(e?.response?.data?.detail || "That file could not be read", "error");
    } finally {
      setImporting(false);
    }
  }

  async function openVersions() {
    setShowVersions(true);
    try {
      setVersions(await getDocTemplateVersions(Number(id)));
    } catch {
      toast("Could not load version history", "error");
    }
  }

  async function promote(versionId: number) {
    try {
      const out = await promoteDocTemplateVersion(Number(id), versionId);
      setLiveVersion(out.live_version ?? null);
      setPending(!!out.has_pending_changes);
      setShowVersions(false);
      toast(`v${out.live_version} is now live`, "success");
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Could not promote that version", "error");
    }
  }

  async function restore(versionId: number) {
    try {
      const out = await restoreDocTemplateVersion(Number(id), versionId);
      setBlocks(out.blocks);
      setVersion(out.version);
      setShowVersions(false);
      toast(`Restored as v${out.version}`, "success");
    } catch {
      toast("Restore failed", "error");
    }
  }

  // AC19: authoring legal wording in a two-pane editor at phone width is not a
  // real workflow. Deliberate, and called out at review so it is not logged as
  // a missing responsive pattern.
  if (isMobile) {
    return (
      <div>
        <div className="page-header"><div><h1>Document Templates</h1></div></div>
        <div style={{
          background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10,
          padding: 20, color: "#92400e", fontSize: 14, lineHeight: 1.6,
        }}>
          <strong>Open this on a larger screen to edit templates.</strong>
          <div style={{ marginTop: 8 }}>
            The editor puts your blocks beside a live preview of the printed letter,
            which needs the width of a tablet or desktop. The template list works here.
          </div>
          <button onClick={() => navigate("/settings?tab=templates")}
            style={{ ...btnSecondary, marginTop: 14 }}>Back to templates</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <LoadingOverlay active={loading || saving || previewing || importing} />

      <div className="page-header">
        <div>
          <h1>{isNew ? "New Template" : name || "Template"}</h1>
          <p>
            {typeLabel}
            {!isNew && <> · editing v{version}</>}
            {liveVersion !== null && <> · <strong>v{liveVersion} is live</strong></>}
            {isDefault && <> · default for this type</>}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button onClick={() => navigate("/settings?tab=templates")} style={btnGhost}>
            Back
          </button>
          {!isNew && (
            <button onClick={openVersions} style={btnSecondary}>Version history</button>
          )}
          {isNew && hasStarter && (
            <button onClick={loadStarter} style={btnSecondary}
              title="Start from reviewed, market-standard wording you can edit">
              Use HRxAI standard
            </button>
          )}
          <button onClick={() => setShowImport(true)} style={btnSecondary}>
            Import a letter
          </button>
          <AsyncButton onClick={save}
            style={blocked ? { ...btnPrimary, background: "#94a3b8", cursor: "not-allowed" }
                           : btnPrimary}>
            {isNew ? "Create template" : `Save as v${version + 1}`}
          </AsyncButton>
        </div>
      </div>

      <div style={{ display: "grid", gap: 12, marginBottom: 16 }}>
        <div style={{ maxWidth: 420 }}>
          <label style={labelStyle}>Template name</label>
          <input value={name} onChange={e => setName(e.target.value)}
            placeholder="Standard Appointment Letter" style={inputStyle} />
        </div>
        <div style={{ fontSize: 12, color: "#64748b" }}>
          {blocks.length} block{blocks.length === 1 ? "" : "s"} ·{" "}
          {blocks.length - aiCount} fixed · {aiCount} written by AI
          {aiCount === 0 && " · uses no AI credits"}
        </div>
      </div>

      {importNote && (
        <div style={{
          background: "#f0f9ff", border: `1px solid ${AI_LINE}`, borderRadius: 8,
          padding: "11px 14px", marginBottom: 14, fontSize: 13, color: AI_FG,
        }}>
          {importNote}
        </div>
      )}

      {pending && (
        <div style={{
          background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8,
          padding: "11px 14px", marginBottom: 14, fontSize: 13, color: "#92400e",
        }}>
          <strong>These changes are not live yet.</strong> Documents are still
          generated from v{liveVersion}.{" "}
          {canPublish
            ? "Promote a version from Version history to make it live."
            : "An admin needs to approve this version before it goes out."}
        </div>
      )}

      {starterNote && (
        <div style={{
          background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8,
          padding: "11px 14px", marginBottom: 14, fontSize: 13, color: "#92400e",
        }}>
          {starterNote}
        </div>
      )}

      {!hasFullName && (
        <div style={{
          background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8,
          padding: "11px 14px", marginBottom: 14, fontSize: 13, color: "#92400e",
        }}>
          <strong>Add {"{{employee_name}}"} to a fixed block.</strong> Documents must name the
          person in full before they can be issued or sent, and {"{{employee_first_name}}"} on
          its own is not enough.
        </div>
      )}

      {problems.length > 0 && (
        <div style={{
          background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
          padding: "12px 14px", marginBottom: 14, fontSize: 13, color: "#991b1b",
        }}>
          <strong>This template cannot be saved yet:</strong>
          <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
            {problems.map((p, i) => <li key={i} style={{ marginBottom: 4 }}>{p}</li>)}
          </ul>
        </div>
      )}

      {/* ── Two panes ── */}
      <div style={{
        display: "grid", gridTemplateColumns: "minmax(0,1.05fr) minmax(0,0.95fr)",
        gap: 16, alignItems: "start",
      }}>
        {/* Blocks */}
        <div style={{
          background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14,
          display: "flex", flexDirection: "column", gap: 10,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{
              fontSize: 11, fontWeight: 700, letterSpacing: ".07em",
              textTransform: "uppercase", color: "#64748b",
            }}>Blocks · {blocks.length}</span>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => addBlock("static")} style={{ ...btnSecondary, padding: "5px 11px", fontSize: 12 }}>
                + Fixed text
              </button>
              <button onClick={() => addBlock("ai")}
                style={{
                  ...btnSecondary, padding: "5px 11px", fontSize: 12,
                  borderColor: AI_LINE, color: AI_FG, background: AI_BG,
                }}>
                + AI block
              </button>
            </div>
          </div>

          {blocks.map((b, i) => {
            const isAi = b.kind === "ai";
            const bad = unknownTokens[b.id] || [];
            return (
              <div key={b.id} style={{
                background: isAi ? AI_BG : "white",
                border: `1px solid ${bad.length ? "#fecaca" : isAi ? AI_LINE : "#e2e8f0"}`,
                borderRadius: 8, padding: 12,
                display: "flex", flexDirection: "column", gap: 8,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                    background: isAi ? "white" : "#f1f5f9",
                    color: isAi ? AI_FG : "#334155",
                    border: `1px solid ${isAi ? AI_LINE : "#cbd5e1"}`,
                  }}>
                    {isAi ? "AI writes" : "Locked"}
                  </span>
                  <input
                    value={b.heading || ""}
                    onChange={e => patch(b.id, { heading: e.target.value })}
                    placeholder="Section heading (optional)"
                    style={{ ...inputStyle, flex: 1, fontWeight: 600 }}
                  />
                  <button onClick={() => move(b.id, -1)} disabled={i === 0}
                    title="Move up" style={{ ...btnGhost, opacity: i === 0 ? 0.3 : 1 }}>↑</button>
                  <button onClick={() => move(b.id, 1)} disabled={i === blocks.length - 1}
                    title="Move down"
                    style={{ ...btnGhost, opacity: i === blocks.length - 1 ? 0.3 : 1 }}>↓</button>
                  <button
                    onClick={() => patch(b.id, {
                      kind: isAi ? "static" : "ai",
                      // Carry the wording across so flipping a block does not
                      // throw away what the author (or the import) already wrote.
                      text: isAi ? (b.fallback_text || b.text || "") : b.text,
                      fallback_text: isAi ? b.fallback_text : (b.text || ""),
                      instruction: isAi ? b.instruction : (b.instruction || ""),
                      max_words: b.max_words || 80,
                    })}
                    style={btnGhost}>
                    {isAi ? "Make fixed" : "Make AI"}
                  </button>
                  <button
                    onClick={() => setBlocks(bs => bs.filter(x => x.id !== b.id))}
                    style={{ ...btnGhost, color: "#dc2626" }}>Remove</button>
                </div>

                {isAi ? (
                  <>
                    <div>
                      <label style={labelStyle}>What should AI write here?</label>
                      <textarea
                        value={b.instruction || ""}
                        onChange={e => patch(b.id, { instruction: e.target.value })}
                        rows={2}
                        placeholder="Two sentences on the scope of the role and reporting line. Do not mention salary."
                        style={{ ...inputStyle, resize: "vertical" }}
                      />
                    </div>
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                      <div style={{ width: 110 }}>
                        <label style={labelStyle}>Max words</label>
                        <input type="number" value={b.max_words || 80}
                          onChange={e => patch(b.id, { max_words: Number(e.target.value) })}
                          style={inputStyle} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>
                          Fallback text — printed if AI is unavailable
                        </label>
                        <textarea
                          value={b.fallback_text || ""}
                          onChange={e => patch(b.id, { fallback_text: e.target.value })}
                          rows={2}
                          placeholder="Your reporting manager will confirm the details of your role."
                          style={{ ...inputStyle, resize: "vertical" }}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ position: "relative" }}>
                    <textarea
                      value={b.text || ""}
                      onChange={e => patch(b.id, { text: e.target.value })}
                      rows={4}
                      placeholder="Your exact wording. Insert fields like {{employee_name}}."
                      style={{ ...inputStyle, resize: "vertical", fontFamily: "monospace", fontSize: 12 }}
                    />
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
                      <button
                        onClick={() => setFieldPickerFor(fieldPickerFor === b.id ? null : b.id)}
                        style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12 }}>
                        + Insert field
                      </button>
                      {b.original_text && (
                        <button onClick={() => patch(b.id, { text: b.original_text })}
                          style={btnGhost} title="Put the imported wording back">
                          Undo field substitutions
                        </button>
                      )}
                    </div>

                    {fieldPickerFor === b.id && (
                      <div style={{
                        position: "absolute", zIndex: 10, top: "100%", left: 0,
                        background: "white", border: "1px solid #cbd5e1", borderRadius: 10,
                        boxShadow: "0 12px 34px rgba(15,23,42,.18)", padding: 12,
                        width: 300, maxHeight: 300, overflowY: "auto",
                        display: "flex", flexDirection: "column", gap: 10,
                      }}>
                        {Object.keys(fields).map(group => (
                          <div key={group}>
                            <div style={{
                              fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                              letterSpacing: ".06em", color: "#64748b", marginBottom: 5,
                            }}>{group}</div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                              {(fields[group] || []).map(f => (
                                <button key={f.token} onClick={() => insertField(b.id, f.token)}
                                  title={`e.g. ${f.example}`}
                                  style={{
                                    fontFamily: "monospace", fontSize: 11,
                                    background: "#eef2ff", color: "#3730a3",
                                    border: "1px solid #c7d2fe", borderRadius: 4,
                                    padding: "2px 6px", cursor: "pointer",
                                  }}>
                                  {f.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {bad.length > 0 && (
                  <div style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>
                    Unknown field{bad.length > 1 ? "s" : ""}: {bad.map(t => `{{${t}}}`).join(", ")} —
                    pick from the field list.
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Preview */}
        <div style={{
          background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14,
          display: "flex", flexDirection: "column", gap: 10, position: "sticky", top: 12,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <span style={{
              fontSize: 11, fontWeight: 700, letterSpacing: ".07em",
              textTransform: "uppercase", color: "#64748b",
            }}>Preview</span>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <select value={previewFor}
                onChange={e => setPreviewFor(e.target.value ? Number(e.target.value) : "")}
                style={{ ...inputStyle, width: "auto", fontSize: 12, padding: "5px 8px" }}>
                <option value="">Sample data</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
              <button onClick={() => runPreview(false)} style={{ ...btnSecondary, padding: "5px 11px", fontSize: 12 }}>
                Preview
              </button>
              <button onClick={openPdfPreview}
                title="Render on your letterhead, as the employee will receive it"
                style={{ ...btnSecondary, padding: "5px 11px", fontSize: 12 }}>
                View as PDF
              </button>
              {aiCount > 0 && (
                <button onClick={() => runPreview(true)}
                  title="Generates the AI blocks for real — uses one AI credit"
                  style={{ ...btnSecondary, padding: "5px 11px", fontSize: 12, borderColor: AI_LINE, color: AI_FG }}>
                  With AI
                </button>
              )}
            </div>
          </div>

          {!preview ? (
            <div style={{
              padding: "40px 20px", textAlign: "center", color: "#64748b",
              fontSize: 13, background: "#f8fafc", borderRadius: 8, lineHeight: 1.6,
            }}>
              Choose an employee and hit Preview to read the letter as they will receive it.
              <div style={{ marginTop: 8, fontSize: 12 }}>
                Merge fields resolve for free. “With AI” generates the AI blocks and spends one credit.
              </div>
            </div>
          ) : (
            <>
              {preview.fell_back.length > 0 && (
                <div style={{
                  fontSize: 12, color: AI_FG, background: AI_BG,
                  border: `1px dashed ${AI_LINE}`, borderRadius: 6, padding: "7px 10px",
                }}>
                  Showing fallback text for: {preview.fell_back.join(", ")}
                </div>
              )}
              <div style={{
                border: "1px solid #e2e8f0", borderRadius: 8, padding: 18,
                fontSize: 12.5, lineHeight: 1.75, color: "#334155",
                whiteSpace: "pre-wrap", maxHeight: 620, overflowY: "auto",
              }}>
                {preview.content}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Import modal ── */}
      {showImport && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}>
          <div style={{
            background: "white", borderRadius: 14, padding: 24, width: "min(700px, 100%)",
            maxHeight: "90vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 14,
          }}>
            <div>
              <h3 style={{ margin: "0 0 4px", fontSize: 17, color: "#0f172a" }}>
                Import a letter you already use
              </h3>
              <p style={{ margin: 0, fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>
                Paste the text, or upload a .docx or .pdf. We'll split it into blocks and
                suggest which parts should stay fixed. Nothing is saved until you review it.
              </p>
            </div>

            <textarea
              value={importText}
              onChange={e => setImportText(e.target.value)}
              rows={14}
              placeholder={"Dear Rahul,\n\nThis confirms the appointment of Rahul Mehta as…"}
              style={{ ...inputStyle, resize: "vertical", fontSize: 12.5, lineHeight: 1.6 }}
            />

            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <label style={{ ...btnSecondary, display: "inline-flex", alignItems: "center" }}>
                Upload .docx / .pdf
                <input type="file" accept=".docx,.pdf" style={{ display: "none" }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(f); }} />
              </label>
              <select value={previewFor}
                onChange={e => setPreviewFor(e.target.value ? Number(e.target.value) : "")}
                style={{ ...inputStyle, width: "auto", fontSize: 12 }}>
                <option value="">Match against sample data</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>Match against {emp.name}</option>
                ))}
              </select>
              <span style={{ fontSize: 12, color: "#64748b" }}>
                Matching against a real employee lets us spot their name and details in the text.
              </span>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setShowImport(false)} style={btnSecondary}>Cancel</button>
              <AsyncButton onClick={runImport} style={btnPrimary}>Analyse</AsyncButton>
            </div>
          </div>
        </div>
      )}

      {/* ── Version history ── */}
      {showVersions && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}>
          <div style={{
            background: "white", borderRadius: 14, padding: 24, width: "min(560px, 100%)",
            maxHeight: "85vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 12,
          }}>
            <h3 style={{ margin: 0, fontSize: 17, color: "#0f172a" }}>Version history</h3>
            <p style={{ margin: 0, fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>
              Restoring writes the old wording as a new version — nothing in this list changes.
              A version marked <em>in use</em> produced a document and is kept permanently.
            </p>
            {versions.map(v => (
              <div key={v.id} style={{
                border: "1px solid #e2e8f0", borderRadius: 8, padding: "11px 13px",
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13.5, color: "#0f172a" }}>
                    v{v.version}
                    {v.is_live && (
                      <span style={{
                        marginLeft: 8, fontSize: 11, fontWeight: 600, padding: "1px 7px",
                        borderRadius: 999, background: "#eff6ff", color: NAVY,
                        border: "1px solid #bfdbfe",
                      }}>live</span>
                    )}
                    {v.in_use && (
                      <span style={{
                        marginLeft: 8, fontSize: 11, fontWeight: 600, padding: "1px 7px",
                        borderRadius: 999, background: "#f0fdf4", color: "#16a34a",
                        border: "1px solid #86efac",
                      }}>in use</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", fontVariantNumeric: "tabular-nums" }}>
                    {v.block_count} blocks · {v.created_by || "unknown"} ·{" "}
                    {v.created_at ? new Date(v.created_at).toLocaleDateString() : ""}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {canPublish && !v.is_live && (
                    <button onClick={() => promote(v.id)}
                      style={{ ...btnPrimary, fontSize: 12, padding: "5px 11px" }}>
                      Make live
                    </button>
                  )}
                  {v.version !== version && (
                    <button onClick={() => restore(v.id)} style={{ ...btnSecondary, fontSize: 12, padding: "5px 11px" }}>
                      Restore
                    </button>
                  )}
                </div>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => setShowVersions(false)} style={btnSecondary}>Close</button>
            </div>
          </div>
        </div>
      )}

      {blocked && !problems.length && (
        <div style={{ marginTop: 14, fontSize: 12, color: "#b45309" }}>
          Resolve the warnings above before saving.
        </div>
      )}
      {!canPublish && !isNew && (
        <div style={{ marginTop: 14, fontSize: 12, color: "#64748b" }}>
          An admin decides which template is live for a document type. You can edit the wording
          here and they can promote it.
        </div>
      )}
    </div>
  );
}
